export const Environments = function () {
  const ENV_VARS = {
    production: 'production',
    at: 'at',
    st: 'st',
    dev: 'dev',
  } as const;

  const AltiboxUrls = {
    production: 'https://api.services.altibox.net',
    at: 'https://api-at3.services.altibox.net',
    st: 'https://api-st1.services.snartibox.net',
  };

  const HuaweiUrls = {
    production: 'https://tvotteds.envision.services.altibox.net',
    at: 'https://tvottedsstg.at.envision.services.altibox.net',
    st: 'https://mrotvottepgottupgtest.st.snartibox.no',
  };

  const AndroidAppIds = {
    production: 'no.altibox.nh',
    at: 'no.altibox.nh',
    st: 'no.altibox.nh',
  };

  const IosAppIds = {
    production: '1164396975',
    at: '1164396975',
    st: '1164396975',
  };

  const IosAppUrls = {
    production: 'itms-apps://itunes.apple.com/no/app/altibox/id1164396975?mt=8',
    at: 'itms-apps://itunes.apple.com/no/app/altibox/id1164396975?mt=8',
    st: 'itms-apps://itunes.apple.com/no/app/altibox/id1164396975?mt=8',
  };

  const ChromecastIDs = {
    production: '1A3E5EF0',
    at: 'FC6EFC61',
    st: '1646497D',
  };

  // ~~~~~~ ATS PROD  ~~~~~~
  const AtsUrls = {
    production: 'https://api.ats.lysetele.cloud',
    at: 'https://api.ats.test.lysetele.cloud',
    dev: 'https://api.ats.dev.lysetele.cloud',
  };

  const CdnUrl = {
    production: 'https://static.cdn.services.altibox.net',
    at: 'https://static.cdn-at.services.altibox.net',
    dev: 'https://cdn.cdn-dev.services.altibox.net',
  };

  const AtsSubscriptionKey = {
    production: '80eeae7ef09e42f0b0ce530a0f03727f',
    at: '73f0128d4a554c1088117a906ced6f96',
    dev: '799b92af7669489cbd3cb915e7608e09',
  };

  function setAppEnv(env: (typeof ENV_VARS)[keyof typeof ENV_VARS]) {
    localStorage.setItem('app_env', env);
  }

  function getAppEnv(): (typeof ENV_VARS)[keyof typeof ENV_VARS] {
    const currentEnv = localStorage.getItem('app_env');

    if (currentEnv !== null && currentEnv in ENV_VARS) {
      return ENV_VARS[currentEnv as keyof typeof ENV_VARS];
    }

    const reactAppEnv = String(process.env.REACT_APP_ENV);
    return reactAppEnv in ENV_VARS ? ENV_VARS[reactAppEnv] : ENV_VARS.production;
  }

  function getAtsSubscriptionKey() {
    const env = getAppEnv();
    if (env === ENV_VARS.st) {
      console.warn('ATS does not have a ST environment!! Using production key.');
      return AtsSubscriptionKey.production;
    }
    return AtsSubscriptionKey[env];
  }

  function setApiToST(env?: number) {
    let url = AltiboxUrls.st;
    if (env) {
      url = replaceApiString(url, env);
    }
    setAltiboxUrl(url);
    setHuaweiToST();
    setAtsToST();
    setAppEnv(ENV_VARS.st);
    _reload();
  }

  function setApiToAT() {
    setHuaweiToAT();
    setAltiboxToAT();
    setAtsToAT();
    setAppEnv(ENV_VARS.at);
    _reload();
  }

  function setApiToProd() {
    setHuaweiToProd();
    setAltiboxToProd();
    setAtsToProd();
    setAppEnv(ENV_VARS.production);
    _reload();
  }

  function setAltiboxToAT(env?: number) {
    let url = AltiboxUrls.at;
    if (env) {
      url = replaceApiString(url, env);
    }
    setAltiboxUrl(url);
  }

  function setAltiboxToProd() {
    setAltiboxUrl(AltiboxUrls.production);
    _reload();
  }

  function setAltiboxToST(env?: number) {
    let url = AltiboxUrls.st;
    if (env) {
      url = replaceApiString(url, env);
    }
    setAltiboxUrl(url);
  }

  function setHuaweiToST() {
    setHuaweiUrl(HuaweiUrls.st);
  }

  function setHuaweiToAT() {
    setHuaweiUrl(HuaweiUrls.at);
  }

  function setHuaweiToProd() {
    setHuaweiUrl(HuaweiUrls.production);
  }

  function setAtsToST() {
    console.warn('ATS does not have a ST environment');
  }

  function setAtsToAT() {
    setCdnUrl(CdnUrl.at);
    setAtsUrl(AtsUrls.at);
  }

  function setAtsToProd() {
    setCdnUrl(CdnUrl.production);
    setAtsUrl(AtsUrls.production);
  }

  function setEnvToST(env?: number) {
    setApiToST(env);
    setHuaweiToST();
  }

  function setEnvToAT() {
    setApiToAT();
    setHuaweiToAT();
    setAtsToAT();
  }

  function setEnvToProd() {
    localStorage.removeItem('aib_api_url');
    localStorage.removeItem('huawei_url');
    localStorage.removeItem('ats_url');
    localStorage.removeItem('cdn_url');
    localStorage.removeItem('app_env');
    _reload();
  }

  function setAltiboxUrl(url: string) {
    localStorage.setItem('aib_api_url', url);
    _reload();
  }

  function getAltiboxUrl() {
    return localStorage.getItem('aib_api_url') || AltiboxUrls.production;
  }

  function setHuaweiUrl(url: string) {
    localStorage.setItem('huawei_url', url);
    _reload();
  }

  function getHuaweiUrl() {
    return localStorage.getItem('huawei_url') || HuaweiUrls[getAppEnv()];
  }

  function setAtsUrl(url: string) {
    localStorage.setItem('ats_url', url);
  }

  function getAtsUrl() {
    return localStorage.getItem('ats_url') || AtsUrls[getAppEnv()];
  }

  function setCdnUrl(url: string) {
    localStorage.setItem('cdn_url', url);
  }

  function getCdnUrl() {
    return localStorage.getItem('cdn_url') || CdnUrl[getAppEnv()];
  }

  function replaceApiString(url: string, replaceNumber: number) {
    const idx = Number(url.search(/\d/));
    if (idx !== -1) {
      let temp = url.split('');
      temp[idx] = replaceNumber.toString();
      return temp.join('');
    } else {
      return url;
    }
  }

  function setCCToProd() {
    setCCId(ChromecastIDs.production);
  }

  function setCCToAT() {
    setCCId(ChromecastIDs.at);
  }

  function setCCToST() {
    setCCId(ChromecastIDs.st);
  }

  function setCCId(id: string) {
    localStorage.setItem('ccid', id);
    _reload();
  }

  function getAndroidAppId() {
    return AndroidAppIds[getAppEnv()];
  }

  function getIosAppId() {
    return IosAppIds[getAppEnv()];
  }

  function getIosAppUrl() {
    return IosAppUrls[getAppEnv()];
  }

  function _reload() {
    document.location.reload();
  }

  return {
    getAndroidAppId,
    getIosAppId,
    getIosAppUrl,

    getAppEnv,
    getAltiboxUrl,
    getHuaweiUrl,
    getAtsUrl,
    getAtsSubscriptionKey,
    getCdnUrl,

    setAltiboxToAT,
    setAltiboxToProd,
    setAltiboxToST,
    setEnvToAT,
    setEnvToST,
    setEnvToProd,
    setApiToST,
    setApiToAT,
    setApiToProd,

    setHuaweiToST,
    setHuaweiToAT,
    setHuaweiToProd,
    setCCToProd,
    setCCToAT,
    setCCToST,
    setCCId,
    setAtsToProd,
    setAtsToAT,
  };
};
